import { UserTypes } from "../api/users.types";

/**
 * Minimal shape needed to render a display name. Kept structural (rather
 * than Pick<UserInterface>) so it accepts both full `UserInterface` rows
 * and the slimmer `ShorUserInterface` used by comments/chat payloads,
 * where `type`/`company` are optional and may arrive only after the
 * backend starts sending them.
 */
interface DisplayNameUser {
  firstName?: string | null;
  lastName?: string | null;
  type?: UserTypes | null;
  company?: { name?: string | null } | null;
}

/**
 * Display name for a user account. Company (JURIDICAL) accounts surface
 * under their company name everywhere; person accounts use "First Last".
 * Falls back to an em-dash for half-filled records so tables never render
 * "undefined undefined".
 *
 * `company` can arrive two ways depending on the payload: nested on the
 * user (full `UserInterface` rows carry `user.company`) or as a SIBLING of
 * the user (the shared comment/chat resources emit `{ user, company }`,
 * matching the mobile contract). Pass the sibling explicitly as the second
 * argument for those payloads; otherwise the nested `user.company` is used.
 */
export const displayUserName = (
  user?: DisplayNameUser | null,
  company?: { name?: string | null } | null,
): string => {
  if (!user) return "-";
  const resolvedCompany = company ?? user.company;
  if (user.type === UserTypes.JURIDICAL && resolvedCompany?.name) {
    return resolvedCompany.name;
  }
  const full = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
  return full || "-";
};
